import type State from '@/classes/state';

interface StateMemory {
    [key: string]: MemoryState;
}

export interface MemoryState {
    selectedFilterIds: Array<string>;
    productSorting: string;
    productLimit: number;
}

class StateMemoryManager {
    private storageKey: string | null;

    constructor() {
        this.storageKey = null;
    }

    /**
     * Saves the state for the given key
     * in the session storage
     * @param key - Key string
     * @param state - State object
     */
    public async save(key: string, state: State): Promise<void> {
        const storageKey = await this.getStorageKey();

        if (!storageKey) {
            return;
        }

        const stateMemory = await this.getStateMemory() || {};
        const memoryState = this.createMemoryState(state);

        stateMemory[key] = memoryState;

        sessionStorage.setItem(storageKey, JSON.stringify(stateMemory));
    }

    /**
     * Reads the MemoryState for the given key
     * from the session storage
     * @param key 
     * @returns Promise which fulfills with the matching 
     * MemoryState or null
     */
    public async getMemoryState(key: string): Promise<MemoryState | null> {
        const stateMemory = await this.getStateMemory();

        if (!stateMemory) {
            return null;
        }

        return stateMemory[key] || null;
    }

    /**
     * Creates a MemoryState object from the given state
     * @param state - App state object
     * @returns The created MemoryState object
     */
    private createMemoryState(state: State): MemoryState {
        return {
            selectedFilterIds: state.selectedFilters.map(filterGroupFilterCombi => filterGroupFilterCombi.filter.id),
            productSorting: state.productSorting,
            productLimit: state.productLimit,
        };
    }

    /**
     * Reads the state memory from the session storage
     * @returns State memory or null
     */
    private async getStateMemory(): Promise<StateMemory | null> {
        const storageKey = await this.getStorageKey();

        if (!storageKey) {
            return null;
        }

        let stateMemory: StateMemory | null;
        
        try {
            const stateMemoryString = sessionStorage.getItem(storageKey);
            stateMemory = stateMemoryString ? JSON.parse(stateMemoryString) : null;
        } catch {
            stateMemory = null;
        }

        return stateMemory;
    }


    /**
     * Creates, caches and returns the session storage key
     * to access the remembered filters
     * @returns Promise which fulfills with the storage key or null
     */
    private async getStorageKey(): Promise<string | null> {
        if (this.storageKey) {
            return this.storageKey;
        }

        await window.Miele.Globals.ready();

        const businessDivision = window.Miele.Globals.get('general.businessDivision');
        const language = window.Miele.Globals.get('general.language');

        if (!businessDivision || !language) {
            return null;
        }

        const storageKey = `miele_plp_state_memo_${businessDivision}_${language}`
        this.storageKey = storageKey;

        return storageKey;
    }
}

export default new StateMemoryManager();